import { useEffect, useMemo, useRef, useState } from "react";
import { HLTVPlayer, PlayerHandle } from "./Player";
import { concepts, getConcept } from "./player/concepts";
import { buildBookmarklet } from "./player/bookmarklet";
import "./review.css";

const countries = [
  ["DE", "Germany"],
  ["US", "United States"],
  ["GB", "United Kingdom"],
  ["CA", "Canada"],
  ["SE", "Sweden"],
  ["DK", "Denmark"],
  ["BR", "Brazil"],
  ["FI", "Finland"],
  ["", "Automatic"],
];
export const HLTVConceptReview = () => {
  const initial = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const search = useRef(initial.toString()).current;
  const player = useRef<PlayerHandle>(null);
  const bookmark = useRef<HTMLAnchorElement>(null);
  const code = useRef<HTMLTextAreaElement>(null);
  const [concept, setConcept] = useState(getConcept(initial.get("concept")));
  const [geo, setGeo] = useState(
    initial.has("geo") ? initial.get("geo")! : "DE",
  );
  const [size, setSize] = useState(
    initial.get("size") === "mobile" ? "mobile" : "desktop",
  );
  const [status, setStatus] = useState("Loading live playlist…");
  const [installStatus, setInstallStatus] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [options, setOptions] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      ["playlist", "region", "previewCampaignId"]
        .filter((k) => initial.get(k))
        .map((k) => [k, initial.get(k)!]),
    ),
  );
  const base = useMemo(() => {
    try {
      const url = new URL(initial.get("base") || window.location.origin);
      if (["https:", "http:"].includes(url.protocol)) return url.origin;
    } catch {
      /* Use this host. */
    }
    return window.location.origin;
  }, [initial]);
  const href = useMemo(
    () =>
      buildBookmarklet({
        base,
        concept: concept.id,
        geo,
        ...options,
        concepts: concepts.map((c, i) => [
          c.id,
          `0${i} · ${c.name}`,
          c.summary,
        ]),
      }),
    [base, concept, geo, options],
  );
  useEffect(() => {
    bookmark.current?.setAttribute("href", href);
  }, [href]);
  useEffect(() => {
    const query = new URLSearchParams({
      concept: concept.id,
      geo,
      ...options,
      size,
    });
    if (base !== window.location.origin) query.set("base", base);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${query}`,
    );
  }, [base, concept, geo, options, size]);
  useEffect(() => {
    if (showCode) code.current?.select();
  }, [showCode]);
  function changeConcept(id: string) {
    setConcept(getConcept(id));
    player.current?.command("concept", id);
  }
  function changeGeo(value: string) {
    setGeo(value);
    setOptions((previous) => {
      const next = { ...previous };
      delete next.region;
      return next;
    });
    player.current?.command("geo", value);
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText(href);
      setInstallStatus("Copied. Paste into a bookmark’s URL field.");
    } catch {
      setShowCode(true);
      setInstallStatus("Select and copy the code below.");
    }
  }
  return (
    <div className="hltv-review">
      <main>
        <header className="review-heading">
          <h1>HLTV player demo</h1>
        </header>
        <section className="demo-controls" aria-label="Demo page controls">
          <div className="section-heading">
            <h2>Demo controls</h2>
          </div>
          <div className="toolbar">
            <label>
              Variant
              <select
                id="concepts"
                aria-describedby="variant-summary"
                value={concept.id}
                onChange={(e) => changeConcept(e.target.value)}
              >
                {concepts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Viewer country
              <select
                id="geo"
                value={geo}
                onChange={(e) => changeGeo(e.target.value)}
              >
                {!countries.some(([id]) => id === geo) && (
                  <option value={geo}>{geo}</option>
                )}
                {countries.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <div className="device-switch" aria-label="Preview size">
              {["desktop", "mobile"].map((value) => (
                <button
                  key={value}
                  aria-pressed={size === value}
                  onClick={() => setSize(value)}
                >
                  {value === "desktop" ? "Desktop" : "Mobile"}
                </button>
              ))}
            </div>
          </div>
          <p className="variant-summary" id="variant-summary">
            {concept.summary}
          </p>
          <div className="playback-tools">
            <span>Jump to</span>
            <button onClick={() => player.current?.command("jump", "start")}>
              Opening
            </button>
            <button
              id="offerJump"
              disabled={["endcard", "native"].includes(concept.id)}
              onClick={() => player.current?.command("jump", "offer")}
            >
              {concept.id === "earned" ? "After the apex" : "Mid-clip offer"}
            </button>
            <button onClick={() => player.current?.command("jump", "endcard")}>
              Clip ending
            </button>
          </div>
        </section>
        <section className="preview-area" aria-label="HLTV player preview">
          <div className="section-heading preview-heading">
            <h2>Player preview</h2>
          </div>
          <div className="stage">
            <div
              id="previewShell"
              className={`preview-shell ${size === "mobile" ? "mobile" : ""}`}
            >
              <div className="publisher-header">
                <span>HIGHLIGHTS</span>
                <span>more »</span>
              </div>
              <div className="publisher-body">
                <HLTVPlayer ref={player} search={search} onStatus={setStatus} />
              </div>
            </div>
          </div>
          <p className="data-status" role="status">
            {status}
          </p>
        </section>
        <div className="installation">
          <a
            ref={bookmark}
            className="install-button"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setInstallStatus(
                "Drag this button to your bookmarks bar, or use Copy bookmarklet.",
              );
            }}
          >
            Add HLTV demo to bookmarks
          </a>
          <button className="copy-button" onClick={copy}>
            Copy bookmarklet
          </button>
          <p>Drag the button to your bookmarks bar, then run it on HLTV.</p>
          <p id="installStatus" role="status">
            {installStatus}
          </p>
          {showCode && (
            <textarea
              ref={code}
              readOnly
              value={href}
              aria-label="Bookmarklet code"
            />
          )}
        </div>
      </main>
    </div>
  );
};
